import { useMemo, useRef, useState, memo, Fragment, useCallback } from 'react';
import { get, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Stack, Box, Grid, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import {
  DragIndicator as DragHandleIcon,
  VisibilityOffOutlined as HiddenIcon,
  FlakyOutlined as ConditionIcon,
  MoreHorizRounded as MenuIcon,
} from '@mui/icons-material';
import {
  editorUtils,
  useNameField,
  useEditorStore,
  LayoutComponentNames,
  LayoutComponentMap,
  getLayoutComponentLabel,
  NodePortalProvider,
  NodePortal,
  Menu,
  IMenuAction,
  LayoutComponentPropertyEditorNames,
  useAssistantLoadingStore,
} from '@diana/core';
import { IComponentBuilderProps } from './ComponentBuilder.types';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { NodePortalWrapper, StyledContainerComponentWrapper } from './ComponentBuilder.styles';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import {
  ContainerTypeComponent,
  useDragLayoutComponent,
  useDropLayoutComponent,
  IDndLayoutComponentDropResult,
  FieldTypeComponent,
  IDndLayoutComponentLocation,
} from '../../../../../providers/DndLayoutComponents2';
import { ComponentNameField } from '../../../Components/ComponentNameField/ComponentNameField';
import { NameFieldWrapper } from '../../../Components/NameFieldWrapper/NameFieldWrapper';
import { useEntityFieldState } from '../../../Support/useEntityFieldState';
import { useDeleteComponent } from '../../../Support/useDeleteComponent';
import { useDuplicateComponent } from '../../../Support/useDuplicateComponent';
import { useOpenComponent } from '../../../Support/useOpenComponent';
import { DropHereBox } from '../../../Components/DropHereBox/DropHereBox';
import { ComponentBuilder } from './ComponentBuilder';
import { ComponentBuilderDropPlaceholder } from './ComponentBuilderDropPlaceholder';
import { useDndComponentHoverEvent } from './useDndComponentHoverEvent';
import { useDndComponentDragEndEvent } from './useDndComponentDragEndEvent';
import { useDndComponentDropEvent } from './useDndComponentDropEvent';
import { DndDropArea } from '../DndDropArea/DndDropArea';

export const ContainerComponentBuilder = memo((props: IComponentBuilderProps) => {
  const {
    layoutId,
    parentId,
    componentId,
    componentName,
    componentSize,
    componentIndex,
    taskId,
    stepId,
    flowId,
  } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette, typography } = useTheme();

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const bodyRef = useRef<HTMLElement>(null);
  const handleRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isDropPlaceholderActive = useFlowBuilder2PageStore((s) => !!s.layoutDndPlaceholderLocation);
  const isDraggingSomething = useFlowBuilder2PageStore((s) => !!s.layoutDndDragItem);
  const isActive = useFlowBuilder2PageStore((s) => {
    if (!s.activeComponent) return false;
    return s.activeComponent.componentId === componentId && s.activeComponent.layoutId === layoutId;
  });

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const component = useEditorStore(
    (s) => s.getLayoutById(layoutId)?.components[componentIndex],
    isEqual,
  );

  const label = component ? getLayoutComponentLabel(component) : '';
  const hasCondition = !!component && !!get(component, 'condition');
  const isInvisible = !!component?.properties.hidden;

  const disabled = hasAnyPendingStatus || isChatLoading;

  const { duplicateComponent } = useDuplicateComponent();
  const { deleteComponent } = useDeleteComponent();

  const openComponent = useOpenComponent();

  const fieldState = useEntityFieldState({
    objectId: componentId,
    objectType: 'layoutComponent',
    fieldName: LayoutComponentPropertyEditorNames.LabelPropertyEditor,
  });
  const isLabelError = fieldState.validationResult?.type === 'error';

  const openComponentEditor = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      if (!isActive) {
        openComponent({ layoutId, componentId });
      }
    },
    [openComponent, isActive, layoutId, componentId],
  );

  const renameSection = useCallback(() => {
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const length = el.value.length;
        el.setSelectionRange(length, length);
      }
    }, 0);
  }, []);

  const {
    onClick: handleLabelClick,
    onDoubleClick: handleLabelDoubleClick,
    onKeyDown,
    onFocus,
    isFocused,
    onBlur,
    isRenaming,
  } = useNameField({
    onClick: openComponentEditor,
    onDoubleClick: renameSection,
    isError: isLabelError,
  });

  const titleFontSize = '16px';

  const menuActions = useMemo<IMenuAction[]>(() => {
    const actions: IMenuAction[] = [];

    actions.push({
      title: t('titles.rename'),
      onClick: ({ close }) => {
        close();
        onFocus();
        renameSection();
      },
    });

    actions.push({
      title: t('titles.duplicate'),
      onClick: ({ close }) => {
        close();
        duplicateComponent({ layoutId, componentId }).catch();
      },
    });

    actions.push({
      title: t('titles.delete'),
      color: palette.error.main,
      onClick: ({ close }) => {
        close();
        deleteComponent({ layoutId, componentId }).catch();
      },
    });

    return actions;
  }, [
    t,
    renameSection,
    componentId,
    deleteComponent,
    duplicateComponent,
    layoutId,
    onFocus,
    palette.error.main,
  ]);

  const { onDndComponentDragEnd } = useDndComponentDragEndEvent();
  const { onDndComponentHover } = useDndComponentHoverEvent({ layoutId, taskId, stepId, flowId });
  const { onDndComponentDrop } = useDndComponentDropEvent({ layoutId, taskId, stepId, flowId });

  const dndLocation = useMemo<IDndLayoutComponentLocation>(() => {
    return { layoutId, parentId, index: componentIndex };
  }, [componentIndex, layoutId, parentId]);

  const dndDropResult = useMemo<IDndLayoutComponentDropResult>(() => {
    return { componentId, componentSize, location: dndLocation };
  }, [componentId, componentSize, dndLocation]);

  const [{ isDragging }, drag, preview] = useDragLayoutComponent<{ isDragging: boolean }>(
    {
      type: ContainerTypeComponent,
      item: () => {
        setIsHovered(false);
        const layout = getLayoutById(layoutId)!;
        const layoutComponent = layout.components[dndLocation.index];

        return {
          origin: dndLocation,
          location: dndLocation,
          component: layoutComponent,
          descendants: editorUtils.getLayoutComponentDescendants({ componentId, layout }),
          originalComponent: layoutComponent,
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (dragObject, monitor) => {
        onDndComponentDragEnd({ dragObject, monitor });
      },
      isDragging: (monitor) => {
        const { component, location } = monitor.getItem();
        return location?.layoutId === layoutId && component.id === componentId;
      },
    },
    [dndLocation, layoutId, componentId, getLayoutById, onDndComponentDragEnd],
  );

  const [{ isOver }, drop] = useDropLayoutComponent<{ isOver: boolean }>(
    {
      accept: ContainerTypeComponent,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (dragObject, monitor) => {
        const isScrollingDuringDnd = useFlowBuilder2PageStore.getState().isScrollingDndDragItem;
        if (isScrollingDuringDnd) return;
        onDndComponentHover({
          monitor,
          dragObject,
          targetElement: bodyRef.current,
          targetDropResult: dndDropResult,
        });
      },
      drop: (dragObject, monitor) => {
        const isScrollingDuringDnd = useFlowBuilder2PageStore.getState().isScrollingDndDragItem;
        if (isScrollingDuringDnd) return;
        return onDndComponentDrop({
          monitor,
          dragObject,
          targetElement: bodyRef.current,
          targetDropResult: dndDropResult,
        });
      },
    },
    [dndDropResult, onDndComponentHover, onDndComponentDrop],
  );

  drag(handleRef);
  preview(bodyRef);
  drop(bodyRef);

  return (
    <Fragment>
      <Box
        onMouseEnter={() => !isDraggingSomething && !isActive && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-layout-component-id={componentId}
        data-layout-component={componentName}
      >
        <StyledContainerComponentWrapper
          ref={bodyRef}
          onClick={openComponentEditor}
          hovered={isHovered}
          active={isActive}
        >
          <NodePortalProvider>
            <Stack direction="row" sx={{ opacity: isDragging ? 0 : 1 }}>
              <NodePortalWrapper>
                <NodePortal name="DRAG_HANDLE">
                  <Collapse
                    in={(isActive || isHovered || isFocused) && !disabled}
                    timeout={150}
                    orientation="horizontal"
                  >
                    <Box
                      ref={handleRef}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ cursor: 'grab', lineHeight: 0 }}
                    >
                      <DragHandleIcon sx={{ width: 20, height: 20 }} />
                    </Box>
                  </Collapse>
                </NodePortal>
              </NodePortalWrapper>

              <NodePortalWrapper>
                <NodePortal name="LABEL">
                  {isRenaming ? (
                    <ComponentNameField
                      data-section-name-field
                      fullWidth
                      multiline
                      layoutId={layoutId}
                      componentId={componentId}
                      componentIndex={componentIndex}
                      fontSize={titleFontSize}
                      variant="standard"
                      placeholder={t('labels.untitledSection')}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={onBlur}
                      onFocus={onFocus}
                      onKeyDown={onKeyDown}
                      inputRef={inputRef}
                      InputProps={{ sx: { p: 0 } }}
                      inputProps={{
                        sx: {
                          minWidth: '250px',
                          fontSize: titleFontSize,
                          borderRadius: 1.5,
                          backgroundColor: palette.background.card,
                        },
                      }}
                      sx={{
                        top: -14,
                        width: '100%',
                        '.MuiInput-root:before': { borderColor: 'transparent' },
                        '.MuiInputBase-input, .MuiInputBase-input::placeholder': {
                          fontWeight: typography.fontWeightMedium,
                        },
                      }}
                    />
                  ) : (
                    <NameFieldWrapper
                      disabled={disabled}
                      onDoubleClick={handleLabelDoubleClick}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLabelClick(e);
                      }}
                    >
                      <Typography
                        sx={({ palette }) => ({
                          ml: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 250,
                          color: !label ? palette.text.placeholder : palette.text.primary,
                          fontWeight: 500,
                          fontSize: 16,
                        })}
                      >
                        {!label ? t('labels.untitledSection') : label}
                      </Typography>
                    </NameFieldWrapper>
                  )}
                </NodePortal>
              </NodePortalWrapper>

              <Box flex={1} maxWidth="100%">
                <ContainerComponentContent
                  {...props}
                  isDragging={isDragging}
                  isRenaming={isRenaming}
                />
              </Box>

              <NodePortalWrapper>
                <NodePortal name="ACTION_BUTTONS">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {hasCondition && (
                      <Box p={0.25} lineHeight={0} title={t('labels.conditionApplied')}>
                        <ConditionIcon fontSize="small" />
                      </Box>
                    )}

                    {isInvisible && (
                      <Box p={0.25} lineHeight={0} title={t('labels.hidden')}>
                        <HiddenIcon fontSize="small" />
                      </Box>
                    )}

                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchor(e.currentTarget);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <MenuIcon sx={{ width: 24, height: 24 }} />
                    </IconButton>
                  </Stack>
                </NodePortal>
              </NodePortalWrapper>
            </Stack>
          </NodePortalProvider>

          {isDragging && !isDropPlaceholderActive && (
            <DropHereBox
              isOver={isOver}
              sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
            />
          )}
        </StyledContainerComponentWrapper>
      </Box>

      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        actions={menuActions}
      />
    </Fragment>
  );
}, isEqual);

const ContainerComponentContent = memo(
  (props: IComponentBuilderProps & { isDragging: boolean; isRenaming: boolean }) => {
    const { layoutId, componentId, componentIndex, taskId, stepId, flowId, isRenaming } = props;

    const components = useEditorStore((s) => s.getLayoutById(layoutId)?.components ?? [], isEqual);

    const items = useMemo(() => {
      return components
        .map((component, index) => {
          const width = component?.properties?.width ?? 'col-12';
          return {
            index,
            parentId: component.parentId,
            componentId: component.id,
            componentName: component.component,
            componentSize: Number(width.replace('col-', '') ?? 12),
          };
        })
        .filter((c) => isEqual(c.parentId, componentId));
    }, [components, componentId]);

    const layoutComponent = useEditorStore(
      (s) => s.getLayoutById(layoutId)?.components[componentIndex],
      isEqual,
    );

    const ReactComponent = useMemo(() => {
      if (layoutComponent?.component) {
        const MatchedComponent = LayoutComponentMap[layoutComponent.component];
        if (MatchedComponent) return MatchedComponent;
      }
      return LayoutComponentMap[LayoutComponentNames.MISSING_COMPONENT];
    }, [layoutComponent?.component]);

    if (!layoutComponent) return <Fragment />;

    return (
      <ReactComponent
        workId=""
        step={{} as any}
        layoutComponent={layoutComponent}
        data={{}}
        index={componentIndex}
        isWorkEditable={false}
        isWorkflowEditMode
        showOverflow={isRenaming}
        renderComponentComponents={() => {
          if (!!items.length)
            return (
              <Fragment>
                {items.map((item) => (
                  <ComponentBuilder
                    key={item.componentId}
                    layoutId={layoutId}
                    parentId={item.parentId}
                    componentId={item.componentId}
                    componentName={item.componentName}
                    componentSize={item.componentSize}
                    componentIndex={item.index}
                    taskId={taskId}
                    stepId={stepId}
                    flowId={flowId}
                  />
                ))}
                <ComponentBuilderDropPlaceholder
                  accept={FieldTypeComponent}
                  location={{
                    layoutId,
                    parentId: componentId,
                    index: items.length ? items[items.length - 1].index + 1 : componentIndex + 1,
                  }}
                  layoutId={layoutId}
                  taskId={taskId}
                  stepId={stepId}
                  flowId={flowId}
                />
              </Fragment>
            );

          return (
            <Grid item xs={12}>
              <DndDropArea
                index={componentIndex + 1}
                layoutId={layoutId}
                parentId={componentId}
                taskId={taskId}
                stepId={stepId}
                flowId={flowId}
              />
            </Grid>
          );
        }}
      />
    );
  },
  isEqual,
);
